import { Product, Award, Testimonial } from '../types';

export const products: Product[] = [
  {
    id: 'pro-8k-projector',
    name: 'Parallax Pro 8K Projector',
    category: 'Projector',
    priceRange: '$12,999.99',
    tagline: 'Redefining visual perfection',
    description: 'The Parallax Pro 8K transcends conventional projection technology. With native 8K resolution and our proprietary lens system, every frame becomes a window into the director\'s vision. Experience color accuracy that rivals the world\'s finest studios, brightness that illuminates even well-lit spaces, and contrast ratios that reveal details invisible to lesser projectors.',
    features: [
      'Native 8K resolution with advanced optics',
      'Laser light source (4,000+ lumens)',
      'Cinema-grade color accuracy (DCI-P3)',
      'Precision lens system with motorized adjustments',
      'Advanced cooling system for silent operation',
      'HDR10+ and Dolby Vision support'
    ],
    specifications: {
      'Resolution': 'Native 8K (7680×4320)',
      'Brightness': '4,000+ lumens',
      'Contrast Ratio': '1,000,000:1',
      'Color Gamut': 'DCI-P3 100%',
      'Light Source': 'Laser Phosphor',
      'Lens': 'Precision motorized zoom/focus',
      'Connections': 'HDMI 2.1, DisplayPort, USB-C',
      'Dimensions': '24" × 18" × 8"',
      'Weight': '45 lbs'
    },
    images: {
      hero: '/images/pro-8k-hero.jpg',
      gallery: ['/images/pro-8k-1.jpg', '/images/pro-8k-2.jpg', '/images/pro-8k-3.jpg']
    },
    awards: ['2026 Innovation Award - Home Theater Excellence', 'Editor\'s Choice - AV Magazine']
  },
  {
    id: 'immersion-surround',
    name: 'Parallax Immersion 5.1 Surround System',
    category: 'Audio System',
    priceRange: '$3,499.99',
    tagline: 'Sound that surrounds, performance that astounds',
    description: 'Immerse yourself in sonic perfection. Our flagship 5.1 surround system delivers reference-quality audio that transforms any space into a premium cinema. Each speaker is meticulously crafted using proprietary acoustic technology and premium materials, creating a soundstage that places you at the heart of every performance.',
    features: [
      '5.1 channel configuration with matched drivers',
      'Proprietary acoustic technology',
      'Handcrafted premium cabinets',
      'Room-specific calibration system',
      'Advanced crossover networks',
      'Reference-grade frequency response'
    ],
    specifications: {
      'Configuration': '5.1 Channel System',
      'Frequency Response': '20Hz - 40kHz',
      'Power Handling': '200W RMS per channel',
      'Sensitivity': '92dB @ 1W/1m',
      'Impedance': '4 ohms nominal',
      'Crossover': '3-way active',
      'Cabinet Material': 'Premium hardwood veneer',
      'Finish Options': 'Ebony, Walnut, Cherry',
      'Dimensions': 'Varies by speaker'
    },
    images: {
      hero: '/images/immersion-hero.jpg',
      gallery: ['/images/immersion-1.jpg', '/images/immersion-2.jpg', '/images/immersion-3.jpg']
    },
    awards: ['Best Surround System 2026', 'Audio Excellence Award']
  },
  {
    id: 'studio-soundbar',
    name: 'Parallax Studio Soundbar',
    category: 'Soundbar',
    priceRange: '$1,899.99',
    tagline: 'Architectural elegance meets acoustic excellence',
    description: 'The Studio Soundbar embodies minimalist luxury without compromising acoustic performance. Featuring studio-grade audio processing and precision-engineered drivers, it delivers expansive soundstage and crystal-clear dialogue. Its sleek architectural design seamlessly integrates into any sophisticated environment.',
    features: [
      'Studio-grade audio processing',
      'Sleek architectural design',
      'Wireless subwoofer integration',
      'Smart room adaptation technology',
      'Premium materials and finishes',
      'Advanced DSP optimization'
    ],
    specifications: {
      'Drivers': '12 precision-matched drivers',
      'Power Output': '300W total system power',
      'Frequency Response': '40Hz - 30kHz',
      'Connectivity': 'HDMI eARC, Optical, Bluetooth 5.0',
      'Subwoofer': 'Wireless 10" active subwoofer',
      'Dimensions': '48" × 3.5" × 2.5"',
      'Weight': '18 lbs (soundbar)',
      'Finish': 'Brushed aluminum, Matte black',
      'Mounting': 'Wall-mount ready'
    },
    images: {
      hero: '/images/studio-hero.jpg',
      gallery: ['/images/studio-1.jpg', '/images/studio-2.jpg', '/images/studio-3.jpg']
    },
    awards: ['Design Excellence Award 2026']
  },
  {
    id: 'vision-screen',
    name: 'Parallax Vision Projection Screen',
    category: 'Projection Screen',
    priceRange: '$2,399.99',
    tagline: 'The perfect canvas for cinematic artistry',
    description: 'The Vision Projection Screen serves as the ultimate canvas for your cinematic experience. Featuring acoustically transparent materials and precision tensioning systems, it delivers flawless image reproduction while allowing sound to pass through unimpeded. Every detail is engineered to showcase your content with uncompromising clarity.',
    features: [
      'Acoustically transparent materials',
      'Custom sizing options',
      'Precision tensioning system',
      'Ambient light rejection technology',
      'Premium installation hardware',
      'Multiple aspect ratio options'
    ],
    specifications: {
      'Material': 'Acoustically transparent weave',
      'Gain': '0.9 - 1.2 (depending on model)',
      'Viewing Angle': '160° horizontal',
      'Light Rejection': 'Advanced ALR coating',
      'Aspect Ratios': '16:9, 2.35:1, 4:3',
      'Sizes': '100" - 200" diagonal',
      'Installation': 'Fixed frame or motorized',
      'Warranty': '10 years material',
      'Maintenance': 'Minimal required'
    },
    images: {
      hero: '/images/vision-hero.jpg',
      gallery: ['/images/vision-1.jpg', '/images/vision-2.jpg', '/images/vision-3.jpg']
    }
  },
  {
    id: 'tower-monitor',
    name: 'Parallax PX 750 Luxury Pro Series',
    category: 'Studio Monitor',
    priceRange: '$3,999.99',
    tagline: 'Commanding presence, uncompromising clarity',
    description: 'The PX 750 Luxury Pro Series delivers studio-grade accuracy in an imposing, floor-standing design. Featuring 1500 watts of total system power, a 6.5-inch full range driver paired with a soft dome silk tweeter, and our signature high gloss wood finish, these monitors fill any room with breathtaking clarity. With wireless connectivity, streaming compatibility, and simple plug-and-play setup, the PX 750 commands your space with effortless authority. Premium luxury stands included.',
    features: [
      '1500W total system output',
      '6.5" full range driver with soft dome silk tweeter',
      'High gloss wood finish cabinet',
      'Wireless connect with streaming compatibility',
      'Simple plug & play setup (iOS and Android)',
      'Premium luxury stands included'
    ],
    specifications: {
      'Model': 'PX 750 Luxury Pro Series',
      'Power Output': '1500 Watts Total System',
      'Driver': '6.5" Full Range',
      'Tweeter': 'Soft Dome Silk',
      'Frequency Response': '45Hz - 20kHz',
      'Impedance': '4 ohms',
      'Cabinet': 'High Gloss Wood Finish',
      'Connectivity': 'Wireless, Streaming (iOS/Android)',
      'Compatibility': 'HDTV Theater',
      'Includes': '2 Studio Monitors + 2 Premium Luxury Stands'
    },
    images: {
      hero: '/images/parallax-tower-monitor-pair.png',
      gallery: ['/images/parallax-tower-monitor-1.jpg', '/images/parallax-tower-monitor-2.jpg']
    },
    awards: ['Studio Monitor Excellence 2026']
  }
];

export const awards: Award[] = [
  {
    title: '2026 Innovation Award',
    organization: 'Home Theater Excellence',
    year: 2026,
    description: 'Recognizing breakthrough technology in projection systems'
  },
  {
    title: 'Editor\'s Choice',
    organization: 'AV Magazine',
    year: 2026,
    description: 'Outstanding performance and value in luxury audio-visual equipment'
  },
  {
    title: 'Best Surround System',
    organization: 'Audio Pro Review',
    year: 2026,
    description: 'Superior acoustic engineering and craftsmanship'
  },
  {
    title: 'Design Excellence Award',
    organization: 'Luxury Home Design',
    year: 2026,
    description: 'Exceptional industrial design and aesthetic integration'
  }
];

export const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Marcus Chen',
    role: 'Home Theater Designer',
    company: 'Elite Installations',
    content: 'The Parallax Pro 8K has completely redefined what I expect from projection technology. The color accuracy and detail reproduction are simply extraordinary. My clients are consistently amazed by the cinematic experience it delivers.',
    products: ['pro-8k-projector', 'vision-screen']
  },
  {
    id: '2',
    name: 'Sarah Williams',
    role: 'Luxury Home Owner',
    content: 'After experiencing the Immersion 5.1 system in our home theater, we can never go back to ordinary audio. The soundstage is incredibly immersive, and the build quality is evident in every detail. It\'s a true work of art.',
    products: ['immersion-surround']
  },
  {
    id: '3',
    name: 'David Rodriguez',
    role: 'Audio Engineer',
    company: 'Precision Sound Labs',
    content: 'The Studio Soundbar achieves something remarkable - it delivers reference-quality audio while maintaining an elegant, minimalist aesthetic. The engineering behind this product is truly impressive.',
    products: ['studio-soundbar']
  }
];

export const navigationItems = [
  { name: 'Home', href: '/' },
  {
    name: 'Products',
    href: '/products',
    dropdown: [
      { name: '8K Projector', href: '/products/pro-8k-projector', description: 'Flagship ultra-premium projector' },
      { name: '5.1 Surround', href: '/products/immersion-surround', description: 'Ultimate home theater audio' },
      { name: 'Soundbar', href: '/products/studio-soundbar', description: 'Luxury minimalist audio' },
      { name: 'Projection Screen', href: '/products/vision-screen', description: 'Premium acoustically transparent screens' },
      { name: 'PX 750 Tower', href: '/products/tower-monitor', description: 'Luxury Pro Series floor-standing monitors' }
    ]
  },
  { name: 'Experience', href: '/experience' },
  { name: 'Warranty', href: '/warranty' },
  { name: 'Contact', href: '/contact' }
]; 